import * as React from 'react'
import { Text, TouchableOpacity, View } from 'react-native'
import styled from '@theme/styled-components'
import theme from '@theme/Theme'

import Icon from '@components/icon/Icons'

import useI18n from '@i18n/useI18n'
import useBreakpoint from '@utils/useBreakpoints'

import { manifestationDate } from '@screens/manifestations/manifestationsUtils'

interface Props {
  list?: boolean
  listWithStat?: boolean
  manifestation: ManifestationResponse
  onClick: () => void
  status?: keyof OrderedManifestations
  stats?: IncidentsStatistiquesResponse
}

const ManifestationCard = ({ list = false, listWithStat = false, manifestation, onClick, status, stats }: Props) => {
  const i18n = useI18n()
  const [, currentBreakpoint] = useBreakpoint()
  const isPhone = currentBreakpoint === 'phone'

  const renderPicto = () => {
    if (status === 'toBeClosed') {
      const hasUnresolvedIncidents = stats?.incidentNonResolu === 0
      return (
        <PictoContainer>
          <Icon
            name={hasUnresolvedIncidents ? 'checked' : 'problem'}
            color={hasUnresolvedIncidents ? theme.colors.closed : theme.colors.alert}
            size={20}
          />
        </PictoContainer>
      )
    }
  }

  if (listWithStat) {
    return (
      <ListContainer shadow key={manifestation.id} activeOpacity={0.9} onPress={onClick}>
        <TopContainer isList>
          <Name isList numberOfLines={2} ellipsizeMode="tail">
            {manifestation.libelle}
          </Name>
          <EventDate isList>{manifestationDate(manifestation, i18n)}</EventDate>
        </TopContainer>
        {stats && (
          <IncidentIndicator list={list}>
            <IncidentIndicatorTotal>{stats.incidentNonResolu}</IncidentIndicatorTotal>
          </IncidentIndicator>
        )}
        {isPhone && (
          <View>
            <Icon name="arrow" size={24} color={theme.colors.primary} />
          </View>
        )}
      </ListContainer>
    )
  }

  if (list) {
    return (
      <ListContainer key={manifestation.id} activeOpacity={0.9} onPress={onClick}>
        <TopContainer isList>
          <Name isList numberOfLines={2} ellipsizeMode="tail">
            {manifestation.libelle}
          </Name>
          <EventDate isList>{manifestationDate(manifestation, i18n)}</EventDate>
        </TopContainer>

        {isPhone && (
          <View>
            <Icon name="arrow" size={24} color={theme.colors.primary} />
          </View>
        )}
      </ListContainer>
    )
  }

  return (
    <Container key={manifestation.id} activeOpacity={0.9} onPress={onClick}>
      <SafeContainer>
        <TopContainer>
          <Name numberOfLines={2} ellipsizeMode="tail">
            {manifestation.libelle}
          </Name>
          <EventDate>{manifestationDate(manifestation, i18n)}</EventDate>
        </TopContainer>
        {stats && (
          <BottomContainer>
            {status && renderPicto()}
            <IncidentTotal>
              <IncidentIndicator>
                <IncidentIndicatorTotal>{stats.incidentNonResolu}</IncidentIndicatorTotal>
              </IncidentIndicator>
              <IncidentLabel>
                {i18n.t('screens.site.incidentsInProgress', { count: stats.incidentNonResolu })}
              </IncidentLabel>
            </IncidentTotal>
          </BottomContainer>
        )}
      </SafeContainer>
    </Container>
  )
}

export default ManifestationCard

const Container = styled(TouchableOpacity)`
  border-radius: 8px;
  width: ${(props) => (props.theme.breakpoint === 'phone' ? 160 : 360)}px;
  ${(props) => props.theme.constants.shadow}
`
const SafeContainer = styled(View)`
  border-radius: 8px;
  overflow: hidden;
`
const ListContainer = styled(TouchableOpacity)<{ shadow?: boolean }>`
  flex-direction: row;
  align-items: center;
  border-radius: 8px;
  overflow: hidden;
  padding-right: 16px;
  background-color: ${(props) => props.theme.colors.background};
  ${(props) => props.theme.breakpoint !== 'phone' && `width: 360px;`}
  ${(props) => (props.theme.breakpoint !== 'phone' || props.shadow) && props.theme.constants.shadow}
`
const TopContainer = styled(View)<{ isList?: boolean }>`
  padding: ${(props) =>
    props.theme.breakpoint === 'phone' ? (props.isList ? '16px' : '16px 16px 8px 16px') : '16px 24px'};
  background-color: ${(props) => props.theme.colors.background};
  ${(props) => props.theme.breakpoint === 'phone' && !props.isList && `text-align: center;`}
  ${(props) => props.isList && `flex: 1;`}
  ${(props) => props.theme.breakpoint !== 'phone' && `min-height: 102px;`}
`

const BottomContainer = styled(View)`
  padding: ${(props) => (props.theme.breakpoint === 'phone' ? 8 : 24)}px;
  background-color: ${(props) =>
    props.theme.breakpoint === 'phone' ? props.theme.colors.background : props.theme.colors.incident};
`

const Name = styled(Text)<{ isList?: boolean }>`
  ${(props) => (props.theme.breakpoint === 'phone' ? props.theme.fonts.Body : props.theme.fonts.Headline)};
  ${(props) =>
    props.theme.breakpoint === 'phone' && !props.isList ? `height: 45px; text-align: center;` : `margin-bottom: 8px;`}
`
const EventDate = styled(Text)<{ isList?: boolean }>`
  ${(props) => props.theme.fonts.Footnote};
  color: ${(props) => props.theme.colors.secondaryText};
  ${(props) => props.theme.breakpoint === 'phone' && !props.isList && ` text-align: center;`}
`

const IncidentTotal = styled(View)`
  border-radius: 6px;
  flex-direction: ${(props) => (props.theme.breakpoint === 'phone' ? 'column' : 'row')};
  align-items: center;
  ${(props) => props.theme.breakpoint === 'phone' && `justify-content: center;`}

  ${(props) =>
    props.theme.breakpoint === 'phone'
      ? `text-align: center;`
      : `
      height: 64px;
      padding: 16px;
      background-color: ${props.theme.colors.linkDisabled};
      `};
`
const IncidentIndicator = styled(View)<{ list?: boolean }>`
  height: ${(props) => (props.theme.breakpoint === 'phone' ? 24 : 32)}px;
  width: ${(props) => (props.theme.breakpoint === 'phone' ? 24 : 32)}px;
  border-radius: 36px;
  background-color: ${(props) =>
    props.theme.breakpoint === 'phone' ? props.theme.colors.secondaryActive : props.theme.colors.primary};
  align-items: center;
  justify-content: center;
  ${(props) => (props.theme.breakpoint === 'phone' ? `margin-bottom: 4px;` : `margin-right: 16px;`)}
  ${(props) => props.list && `margin-right: 16px;`}
`
const IncidentIndicatorTotal = styled(Text)`
  ${(props) => (props.theme.breakpoint === 'phone' ? props.theme.fonts.Caption1 : props.theme.fonts.Subheadline)};
  text-align: center;
  color: ${(props) =>
    props.theme.breakpoint === 'phone' ? props.theme.colors.primaryText : props.theme.colors.background};
  align-items: center;
`
const IncidentLabel = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  color: ${(props) =>
    props.theme.breakpoint === 'phone' ? props.theme.colors.secondaryText : props.theme.colors.primary};
  font-size: ${(props) => (props.theme.breakpoint === 'phone' ? 11 : 16)}px;
`

const PictoContainer = styled(View)`
  position: absolute;
  right: 24px;
  top: -10px;
`
